// pages/About.jsx
import React, {useState, useEffect, useLayoutEffect} from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { FiGlobe, FiDroplet, FiStar, FiHeart, FiUsers, FiAward } from 'react-icons/fi';
import { GiChemicalDrop, GiLeafSwirl, GiFlowerPot } from 'react-icons/gi';
import demoImage from "../assets/images/demo.png";

const AboutPage = () => {
    const [isScrolled, setIsScrolled] = useState(false);


    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const brandMilestones = [
        {
            year: "2010",
            title: "Foundation",
            description: "Founded in Milan by master perfumers and skincare scientists",
            icon: <FiStar className="w-6 h-6" />
        },
        {
            year: "2013",
            title: "Global Expansion",
            description: "First international flagship store opens in Paris",
            icon: <FiGlobe className="w-6 h-6" />
        },
        {
            year: "2016",
            title: "Innovation Lab",
            description: "State-of-the-art research facility established in Kyoto",
            icon: <GiChemicalDrop className="w-7 h-7" />
        },
        {
            year: "2019",
            title: "Sustainability Pledge",
            description: "100% sustainable sourcing and carbon-neutral operations",
            icon: <GiLeafSwirl className="w-7 h-7" />
        },
        {
            year: "2022",
            title: "Luxury Certification",
            description: "Awarded Luxury Beauty Brand of the Year",
            icon: <FiAward className="w-6 h-6" />
        },
        {
            year: "Present",
            title: "Global Community",
            description: "Serving clients in over 50 countries worldwide",
            icon: <FiUsers className="w-6 h-6" />
        }
    ];

    const philosophyPillars = [
        {
            icon: <GiFlowerPot className="w-8 h-8" />,
            title: "Intelligent Botanicals",
            description: "Sourcing rare botanicals from sustainable ecosystems, preserving biodiversity while extracting nature's most potent compounds.",
            gradient: "from-muted-sage/20 to-muted-sage/5"
        },
        {
            icon: <GiChemicalDrop className="w-8 h-8" />,
            title: "Precision Science",
            description: "Advanced molecular research ensures each ingredient delivers targeted, visible results with clinical efficacy.",
            gradient: "from-champagne-gold/20 to-champagne-gold/5"
        },
        {
            icon: <FiDroplet className="w-8 h-8" />,
            title: "Ritual Formulation",
            description: "Following chronobiology principles to harmonize skincare with your skin's natural rhythms for optimal absorption.",
            gradient: "from-muted-sage/20 to-muted-sage/5"
        }
    ];

    const founderTeam = [
        {
            name: "Isabella Rossi",
            role: "Founder & Master Perfumer",
            background: "Former lead perfumer at legendary Milanese houses, bringing 25 years of olfactory expertise.",
            image: demoImage
        },
        {
            name: "Dr. Kenji Tanaka",
            role: "Chief Scientific Officer",
            background: "PhD in Dermatological Science from Kyoto University, pioneer in botanical extraction techniques.",
            image: demoImage
        },
        {
            name: "Sophie Laurent",
            role: "Creative Director",
            background: "Former Vogue Paris beauty editor, shaping the visual language of luxury skincare.",
            image: demoImage
        }
    ];

    const sustainabilityCommitments = [
        {
            icon: "♻️",
            title: "Zero-Waste Packaging",
            description: "100% recyclable and 85% post-consumer recycled materials"
        },
        {
            icon: "🌱",
            title: "Regenerative Farming",
            description: "Partnering with growers who practice soil regeneration"
        },
        {
            icon: "💧",
            title: "Water Stewardship",
            description: "Closed-loop water systems in all production facilities"
        },
        {
            icon: "🌍",
            title: "Carbon Neutral",
            description: "150% carbon offset through reforestation projects"
        }
    ];

    return (
        <div className="min-h-screen bg-pearl-white">
            {/* Fixed Navbar */}
            <div
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    isScrolled
                        ? 'bg-white/100 backdrop-blur-xl shadow-soft'
                        : 'bg-transparent'
                }`}
            >
                <Navbar />
            </div>

            {/* Hero Section - Luxury Editorial */}
            <section id="hero" className="pt-24 relative overflow-hidden">
                <div className="absolute inset-0">
                    {/* Background Gradient */}
                    <img
                        src={demoImage}
                        alt="KROMAIC Brand Essence"
                        className="absolute inset-0 w-full h-full object-cover opacity-30"
                    />
                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
                        <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-champagne-gold blur-3xl" />
                        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 rounded-full bg-muted-sage blur-3xl" />
                    </div>

                    {/* Subtle Pattern */}
                    <div className="absolute inset-0 opacity-[0.02]"
                         style={{
                             backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%239caf88' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                             backgroundSize: '300px'
                         }}
                    />
                </div>

                <div className="container-luxury relative z-10">
                    <div className="max-w-5xl mx-auto pt-20 pb-32">
                        <motion.div
                            initial={{ opacity: 0, y: 60 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                            className="text-center"
                        >
                            {/* Brand Mark */}
                            <div className="inline-flex items-center gap-3 mb-8">
                                <div className="h-px w-12 bg-champagne-gold" />
                                <span className="font-poppins text-sm uppercase tracking-widest text-muted-sage">
                  The Art of Beauty
                </span>
                                <div className="h-px w-12 bg-champagne-gold" />
                            </div>

                            {/* Main Title */}
                            <h1 className="font-playfair text-7xl md:text-8xl lg:text-9xl text-deep-charcoal mb-8 leading-none">
                                KROMAIC
                            </h1>

                            {/* Subtitle */}
                            <div className="max-w-2xl mx-auto">
                                <p className="font-poppins text-3xl text-charcoal-60 leading-relaxed mb-12">
                                    Where beauty becomes a ritual, and luxury finds purpose.
                                </p>
                            </div>

                            {/* Decorative Quote */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 1, delay: 0.8 }}
                                className="mt-16"
                            >
                                <div className="inline-block relative">
                                    <div className="absolute -top-6 -left-6 text-6xl text-champagne-gold opacity-30">"</div>
                                    <p className="font-cormorant text-2xl text-charcoal-60 italic px-12">
                                        True transformation begins where science meets soul
                                    </p>
                                    <div className="absolute -bottom-6 -right-6 text-6xl text-champagne-gold opacity-30">"</div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>

                {/* Decorative Border */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-nude-beige to-transparent" />
            </section>

            {/* Brand Story - Editorial Layout */}
            <section id="story" className="section-spacing">
                <div className="container-luxury">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        {/* Story Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="lg:pr-12"
                        >
                            <div className="inline-flex items-center gap-4 mb-8">
                                <div className="h-px w-8 bg-muted-sage" />
                                <span className="font-poppins text-sm uppercase tracking-widest text-muted-sage">
                  Our Journey
                </span>
                            </div>

                            <h2 className="font-playfair text-5xl md:text-6xl text-deep-charcoal mb-8">
                                The KROMAIC Story
                            </h2>

                            <div className="space-y-6">
                                <p className="font-poppins text-lg text-charcoal-60 leading-relaxed">
                                    Founded in Milan and refined through global beauty rituals, KROMAIC emerged from a simple truth: true luxury lies in transformation, not just decoration.
                                </p>

                                <p className="font-poppins text-lg text-charcoal-60 leading-relaxed">
                                    Our journey began with master perfumers and skincare scientists collaborating across Paris, Kyoto, and New York. We observed that the world's most meaningful beauty rituals share a common thread—they honor both science and soul.
                                </p>

                                <p className="font-poppins text-lg text-charcoal-60 leading-relaxed">
                                    Today, KROMAIC represents a new chapter in cosmetic elegance. Where every formulation tells a story, every texture offers an experience, and every detail serves a purpose beyond the surface.
                                </p>
                            </div>
                        </motion.div>

                        {/* Story Image */}
                        <motion.div
                            initial={{ opacity: 0, x: 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="relative rounded-3xl overflow-hidden shadow-luxe">
                                <div className="aspect-[4/5] bg-gradient-to-br from-nude-beige to-soft-ivory" >
                                    <img
                                        src={demoImage}
                                        alt="KROMAIC Origins"
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-deep-charcoal/20 to-transparent" />

                                {/* Decorative Frame */}
                                <div className="absolute -inset-4 border-2 border-nude-beige/30 rounded-3xl -z-10" />
                            </div>

                            {/* Floating Element */}
                            <motion.div
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                                className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-luxe max-w-xs"
                            >
                                <div className="text-3xl mb-2">✨</div>
                                <h4 className="font-cormorant text-xl text-deep-charcoal mb-2">
                                    Milan, 2010
                                </h4>
                                <p className="font-poppins text-sm text-charcoal-60">
                                    Where it all began
                                </p>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Philosophy Pillars */}
            <section id="philosophy" className="section-spacing bg-soft-ivory/50">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                    >
                        <div className="text-center mb-16">
                            <div className="inline-flex items-center gap-4 mb-6">
                                <div className="h-px w-12 bg-champagne-gold" />
                                <span className="font-poppins text-sm uppercase tracking-widest text-champagne-gold">
                  Our Philosophy
                </span>
                                <div className="h-px w-12 bg-champagne-gold" />
                            </div>

                            <h2 className="font-playfair text-5xl md:text-6xl text-deep-charcoal mb-8">
                                Where Nature Meets Science
                            </h2>
                            <p className="font-poppins text-xl text-charcoal-60 max-w-3xl mx-auto">
                                Three pillars that define our approach to beauty
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {philosophyPillars.map((pillar, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    whileHover={{ y: -8 }}
                                    className="relative"
                                >
                                    {/* Background Number */}
                                    <div className="absolute -top-6 -left-6 font-playfair text-7xl text-nude-beige font-bold opacity-30 z-0">
                                        {String(index + 1).padStart(2, '0')}
                                    </div>

                                    <div className={`bg-gradient-to-br ${pillar.gradient} backdrop-blur-sm rounded-2xl 
                    p-8 relative z-10 border border-white/50 shadow-soft hover:shadow-lift 
                    transition-all duration-500 h-full`}
                                    >
                                        <div className="w-16 h-16 rounded-xl bg-white/80 flex items-center justify-center
                      text-deep-charcoal mb-6 shadow-soft">
                                            {pillar.icon}
                                        </div>

                                        <h3 className="font-cormorant text-2xl text-deep-charcoal mb-4">
                                            {pillar.title}
                                        </h3>
                                        <p className="font-poppins text-charcoal-60 leading-relaxed">
                                            {pillar.description}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Founder Team */}
            <section id="team" className="section-spacing">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                    >
                        <div className="text-center mb-16">
                            <h2 className="font-playfair text-5xl text-deep-charcoal mb-8">
                                The Visionaries
                            </h2>
                            <p className="font-poppins text-xl text-charcoal-60 max-w-3xl mx-auto">
                                Meet the minds shaping the future of luxury skincare
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {founderTeam.map((member, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className="group"
                                >
                                    <div className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-lift
                    transition-all duration-500 border border-nude-beige/30"
                                    >
                                        {/* Image Container */}
                                        <div className="aspect-square bg-gradient-to-br from-nude-beige to-soft-ivory
                      relative overflow-hidden"
                                        >
                                            <img
                                                src={member.image}
                                                alt={member.name}
                                                className="absolute inset-0 w-full h-full object-cover"
                                            />

                                            {/* Member Info Overlay */}
                                            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-deep-charcoal/80 to-transparent">
                                                <h3 className="font-cormorant text-2xl text-white mb-1">
                                                    {member.name}
                                                </h3>
                                                <p className="font-poppins text-sm text-champagne-gold">
                                                    {member.role}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Member Bio */}
                                        <div className="p-6">
                                            <p className="font-poppins text-charcoal-60 leading-relaxed">
                                                {member.background}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Brand Milestones */}
            <section id="milestones" className="section-spacing bg-nude-beige/30">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                    >
                        <div className="text-center mb-16">
                            <h2 className="font-playfair text-5xl text-deep-charcoal mb-8">
                                Our Journey Through Time
                            </h2>
                            <p className="font-poppins text-xl text-charcoal-60 max-w-3xl mx-auto">
                                Key moments that defined the KROMAIC legacy
                            </p>
                        </div>

                        <div className="relative">
                            {/* Timeline Line */}
                            <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-gradient-to-r from-muted-sage/20 via-champagne-gold/20 to-muted-sage/20 -translate-y-1/2 hidden lg:block" />

                            <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
                                {brandMilestones.map((milestone, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 40 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        className="relative"
                                    >
                                        {/* Timeline Dot */}
                                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-6 bg-white border-4 border-muted-sage
                      rounded-full z-10 hidden lg:block" />

                                        <div className="bg-white rounded-2xl p-6 shadow-soft mt-8 lg:mt-12 text-center">
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-muted-sage/10 to-champagne-gold/10
                        flex items-center justify-center text-muted-sage mx-auto mb-4">
                                                {milestone.icon}
                                            </div>

                                            <div className="font-playfair text-3xl text-champagne-gold mb-2">
                                                {milestone.year}
                                            </div>

                                            <h3 className="font-cormorant text-xl text-deep-charcoal mb-3">
                                                {milestone.title}
                                            </h3>
                                            <p className="font-poppins text-sm text-charcoal-60">
                                                {milestone.description}
                                            </p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Sustainability */}
            <section id="sustainability" className="section-spacing">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="max-w-4xl mx-auto text-center"
                    >
                        <div className="bg-gradient-to-br from-white to-pearl-white rounded-3xl p-12 shadow-soft
              border border-nude-beige/30"
                        >
                            <div className="inline-flex items-center gap-3 mb-8">
                                <FiHeart className="w-8 h-8 text-muted-sage" />
                                <span className="font-poppins text-sm uppercase tracking-widest text-muted-sage">
                  Our Commitment
                </span>
                            </div>

                            <h2 className="font-playfair text-5xl text-deep-charcoal mb-8">
                                Sustainable Luxury
                            </h2>

                            <p className="font-poppins text-xl text-charcoal-60 mb-12 leading-relaxed">
                                At KROMAIC, we believe true luxury must be sustainable. Our commitment extends beyond
                                exceptional formulations to ethical practices that honor both people and planet.
                            </p>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                {sustainabilityCommitments.map((commitment, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                                        className="bg-soft-ivory p-6 rounded-2xl"
                                    >
                                        <div className="text-3xl mb-3">{commitment.icon}</div>
                                        <h4 className="font-cormorant text-lg text-deep-charcoal mb-2">
                                            {commitment.title}
                                        </h4>
                                        <p className="font-poppins text-sm text-charcoal-60">
                                            {commitment.description}
                                        </p>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Closing Vision */}
            <section id="vision" className="section-spacing bg-gradient-to-b from-soft-ivory to-pearl-white">
                <div className="container-luxury">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="max-w-4xl mx-auto text-center"
                    >
                        <div className="mb-16">
                            <h2 className="font-playfair text-5xl md:text-6xl text-deep-charcoal mb-8">
                                Our Vision Forward
                            </h2>
                            <p className="font-poppins text-2xl text-charcoal-60 leading-relaxed mb-12">
                                We envision a world where beauty standards evolve from perfection to authenticity,
                                where skincare is understood as self-care, and where luxury is measured not by price,
                                but by the depth of experience and integrity of ingredients.
                            </p>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="space-y-8"
                        >
                            <motion.button
                                whileHover={{ scale: 1.05, y: -2 }}
                                whileTap={{ scale: 0.98 }}
                                className="px-12 py-4 bg-deep-charcoal text-white font-poppins text-lg rounded-full
                  hover:bg-deep-charcoal/90 transition-all duration-300 shadow-soft hover:shadow-lift
                  flex items-center gap-3 mx-auto"
                            >
                                Discover Our Formulations
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            </motion.button>

                            <div className="inline-flex items-center gap-6">
                                <div className="h-px w-12 bg-gradient-to-r from-transparent to-champagne-gold" />
                                <span className="font-cormorant text-2xl text-champagne-gold italic">
                  The Future of Beauty Awaits
                </span>
                                <div className="h-px w-12 bg-gradient-to-l from-transparent to-champagne-gold" />
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default AboutPage;